import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { acceptSession, type AuthResponse } from "../lib/session";
import { useAuthStore } from "../store/auth";
import { ErrorText, Field, inputCls } from "../components/ui";

type InviteInfo = { org_name?: string; role?: "org-admin" | "teacher" };

export function InvitePage() {
  const { t } = useTranslation(["common", "admin"]);
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const info = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api<InviteInfo>(`/api/invitations/${encodeURIComponent(token)}`),
    enabled: !!token,
    retry: false,
  });
  const invalid = info.error instanceof ApiError && (info.error.status === 404 || info.error.status === 410);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("admin:invite_password_short"));
      return;
    }
    setSending(true);
    useAuthStore.getState().clear();
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "invitation" });
    try {
      const data = await api<AuthResponse>(`/api/invitations/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        body: { email: email.trim(), name: name.trim(), password },
      });
      const home = acceptSession(data);
      if (!home) {
        setError(t("admin:login_not_admin"));
        return;
      }
      navigate(home, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common:error_generic"));
    } finally {
      setSending(false);
    }
  }

  const roleLabel = info.data?.role === "org-admin" ? t("common:role_org_admin") : info.data?.role === "teacher" ? t("common:role_teacher") : "";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-br from-gray-50 to-sakura/20">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <div className="text-xs text-muted">{t("common:app_name")}</div>
        <h1 className="text-xl font-bold text-primary mt-1">{t("admin:invite_title")}</h1>

        {invalid ? (
          <div className="mt-6">
            <ErrorText>{t("admin:invite_invalid")}</ErrorText>
          </div>
        ) : (
          <>
            {info.data?.org_name && roleLabel && (
              <p className="text-sm text-body mt-3">{t("admin:invite_for", { org: info.data.org_name, role: roleLabel })}</p>
            )}
            <form onSubmit={onSubmit} className="space-y-4 mt-6">
              <Field label={t("common:email")}>
                <input data-testid="invite-email" type="email" autoComplete="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("admin:invite_name")}>
                <input data-testid="invite-name" type="text" autoComplete="name" required maxLength={80} value={name}
                  onChange={(e) => setName(e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("common:password")} hint={t("admin:invite_password_hint")}>
                <input data-testid="invite-password" type="password" autoComplete="new-password" required minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
              </Field>
              {error && <ErrorText testId="invite-error">{error}</ErrorText>}
              <button data-testid="invite-submit" type="submit" disabled={sending}
                className="w-full bg-ops text-white py-2 rounded-lg hover:bg-ops/90 disabled:opacity-50">
                {sending ? t("common:loading") : t("admin:invite_submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
